import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import algoliasearch from 'algoliasearch';
import { of, throwError } from 'rxjs';
import { mergeMap, map, shareReplay } from 'rxjs/operators';

const ALGOLIA_SERVICE_TOKEN = new InjectionToken('ALGOLIA_SERVICE_TOKEN');
const ALGOLIA_CREDENTIALS_FETCHERS_TOKEN = new InjectionToken('ALGOLIA_SERVICE_TOKEN');

class AlgoliaClientService {
    constructor(algoliaParams) {
        this.client = algoliasearch(algoliaParams.applicationId, algoliaParams.apiKey);
    }
}
AlgoliaClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.2", ngImport: i0, type: AlgoliaClientService, deps: [{ token: ALGOLIA_SERVICE_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable });
AlgoliaClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.2", ngImport: i0, type: AlgoliaClientService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.2", ngImport: i0, type: AlgoliaClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [ALGOLIA_SERVICE_TOKEN]
                }] }]; } });

class AlgoliaModule {
}
AlgoliaModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.2", ngImport: i0, type: AlgoliaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AlgoliaModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.2", ngImport: i0, type: AlgoliaModule, imports: [CommonModule] });
AlgoliaModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.2", ngImport: i0, type: AlgoliaModule, providers: [AlgoliaClientService], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.2", ngImport: i0, type: AlgoliaModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    providers: [AlgoliaClientService],
                }]
        }] });

class AlgoliaClientProvider {
    constructor(credentialsFetchers) {
        this.credentialsFetchers = credentialsFetchers;
        this.clientFactories = new Map();
    }
    /**
     * The `keyId` should exists in the credentialsFetchers passed to the constructor.
     */
    getClient(keyId) {
        return this.getClientFactory(keyId)
            .pipe(mergeMap((f) => {
            if (f.expired) {
                this.clientFactories.delete(this.credentialsFetchers[keyId]);
                return this.getClientFactory(keyId);
            }
            return of(f);
        }))
            .pipe(map((f) => f.get(keyId)));
    }
    getClientFactory(keyId) {
        if (!this.credentialsFetchers[keyId]) {
            return throwError(`No credentials fetcher defined for key: ${keyId}`);
        }
        const fetcher = this.credentialsFetchers[keyId];
        if (!this.clientFactories.has(fetcher)) {
            this.clientFactories.set(fetcher, fetcher
                .fetchCredentials()
                .pipe(map((c) => new ClientFactory(c)))
                .pipe(shareReplay()));
        }
        return this.clientFactories.get(fetcher);
    }
}
AlgoliaClientProvider.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.2", ngImport: i0, type: AlgoliaClientProvider, deps: [{ token: ALGOLIA_CREDENTIALS_FETCHERS_TOKEN }], target: i0.ɵɵFactoryTarget.Injectable });
AlgoliaClientProvider.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.2", ngImport: i0, type: AlgoliaClientProvider, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.2", ngImport: i0, type: AlgoliaClientProvider, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [ALGOLIA_CREDENTIALS_FETCHERS_TOKEN]
                }] }]; } });
/**
 * Only create 1 instance of algolia.Client per index
 */
class ClientFactory {
    constructor(credentials) {
        this.credentials = credentials;
        this.clientsCache = {};
    }
    /**
     * Creates algolia clients.
     *
     * The passed key ID should exists in the credentials
     * given in the constructor.
     */
    get(keyId) {
        if (!this.clientsCache[keyId]) {
            const apiKey = this.credentials.apiKeys.find((e) => e.id === keyId);
            if (apiKey === undefined) {
                throw new Error(`Cannot find key with id: ${keyId}.`);
            }
            this.clientsCache[keyId] = algoliasearch(this.credentials.appId, apiKey.key);
        }
        return this.clientsCache[keyId];
    }
    get expired() {
        return Math.floor(Date.now() / 1000) > this.credentials.expires; // 30secs before actual expiration
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { ALGOLIA_CREDENTIALS_FETCHERS_TOKEN, ALGOLIA_SERVICE_TOKEN, AlgoliaClientProvider, AlgoliaClientService, AlgoliaModule };
